// Per-file encryption for chat file sharing (Layer CH). A fresh AES-GCM key
// per file, ciphertext to object storage, key+nonce+hash carried inside the
// E2EE message (Signal/Matrix pattern). Nothing here ever reaches the server
// except opaque ciphertext.

function toB64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function fromB64(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export interface EncryptedFile {
  ciphertext: Uint8Array<ArrayBuffer>;
  key: string; // base64 raw AES-256 key
  nonce: string; // base64 12-byte IV
  hash: string; // base64 SHA-256 of ciphertext
}

/** Encrypt file bytes under a fresh key; returns the ciphertext to upload and
 * the key/nonce/hash to put in the (E2EE) message. */
export async function encryptFile(data: ArrayBuffer): Promise<EncryptedFile> {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ctBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  const ciphertext = new Uint8Array(ctBuf) as Uint8Array<ArrayBuffer>;
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', key));
  const hashBuf = await crypto.subtle.digest('SHA-256', ctBuf);
  return {
    ciphertext,
    key: toB64(raw),
    nonce: toB64(iv),
    hash: toB64(new Uint8Array(hashBuf)),
  };
}

/** Verify the ciphertext hash, then decrypt. A hash mismatch means the blob
 * was swapped or corrupted — refuse before decrypting. */
export async function decryptFile(
  ciphertext: ArrayBuffer,
  keyB64: string,
  nonceB64: string,
  hashB64: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const actual = new Uint8Array(await crypto.subtle.digest('SHA-256', ciphertext));
  if (toB64(actual) !== hashB64) throw new Error('file integrity check failed');
  const key = await crypto.subtle.importKey('raw', fromB64(keyB64), 'AES-GCM', false, ['decrypt']);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromB64(nonceB64) },
    key,
    ciphertext,
  );
  return new Uint8Array(plain) as Uint8Array<ArrayBuffer>;
}
